import React, { useState, useEffect } from 'react'
import { Search, Filter, Plus, Edit, Trash2, Eye, Calendar, User, Tag, FileText, Download, Upload, X } from 'lucide-react'
import { articlesData } from '../data/articlesData'
import { articleService, formatArticleDate } from '../services/articleService'
import { useUser } from '../context/UserContext'

const Articles = ({ currentPage: parentCurrentPage, itemsPerPage: parentItemsPerPage, onPageChange, onItemsPerPageChange }) => {
  const { canCreate, canEdit, canDelete } = useUser()
  const [articles, setArticles] = useState([])
  const [filteredArticles, setFilteredArticles] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedDifficulty, setSelectedDifficulty] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [editingArticle, setEditingArticle] = useState(null)
  const [viewingArticle, setViewingArticle] = useState(null)
  const [loading, setLoading] = useState(false)
  const [attachments, setAttachments] = useState([])
  const [uploadingFiles, setUploadingFiles] = useState(false)
  const [formErrors, setFormErrors] = useState({})
  
  // Use parent pagination state if provided, otherwise use local state
  const [localCurrentPage, setLocalCurrentPage] = useState(1)
  const [localItemsPerPage, setLocalItemsPerPage] = useState(6)
  
  const currentPage = parentCurrentPage !== undefined ? parentCurrentPage : localCurrentPage
  const itemsPerPage = parentItemsPerPage !== undefined ? parentItemsPerPage : localItemsPerPage

  // Load articles from database
  const loadArticles = async () => {
    try {
      setLoading(true)
      const data = await articleService.getAllArticles()
      
      // Load attachments for each article
      const articlesWithAttachments = await Promise.all(
        data.map(async (article) => {
          try {
            const attachments = await articleService.getArticleAttachments(article.id)
            return { ...article, attachments }
          } catch (error) {
            console.error(`Error loading attachments for article ${article.id}:`, error)
            return { ...article, attachments: [] }
          }
        })
      )
      
      setArticles(articlesWithAttachments)
    } catch (error) {
      console.error('Error loading articles:', error)
      // Fallback to local data if database fails
      setArticles(articlesData)
    } finally {
      setLoading(false)
    }
  }

  // Load articles on component mount
  useEffect(() => {
    loadArticles()
  }, [])
  
  // Calculate pagination
  const totalItems = filteredArticles.length
  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentArticles = filteredArticles.slice(startIndex, endIndex)
  
  // Reset to first page when filters change
  useEffect(() => {
    if (onPageChange) {
      onPageChange(1)
    } else {
      setLocalCurrentPage(1)
    }
  }, [searchTerm, selectedCategory, selectedDifficulty, selectedStatus, onPageChange])

  const [articleForm, setArticleForm] = useState({
    title: '',
    category: '',
    difficulty: 'beginner',
    status: 'draft',
    featured: false,
    excerpt: '',
    content: '',
    tags: []
  })

  // Filter articles based on search and filters
  useEffect(() => {
    let filtered = articles

    if (searchTerm) {
      filtered = filtered.filter(article =>
        article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    }

    if (selectedCategory) {
      filtered = filtered.filter(article => article.category === selectedCategory)
    }

    if (selectedDifficulty) {
      filtered = filtered.filter(article => article.difficulty === selectedDifficulty)
    }

    if (selectedStatus) {
      filtered = filtered.filter(article => article.status === selectedStatus)
    }

    setFilteredArticles(filtered)
  }, [articles, searchTerm, selectedCategory, selectedDifficulty, selectedStatus])

  const categories = [...new Set(articles.map(article => article.category))]
  const difficulties = ['beginner', 'intermediate', 'advanced']
  const statuses = ['draft', 'published']



  const handleCreateArticle = () => {
    setArticleForm({
      title: '',
      category: '',
      difficulty: 'beginner',
      status: 'draft',
      featured: false,
      excerpt: '',
      content: '',
      tags: []
    })
    setAttachments([])
    setFormErrors({})
    setShowCreateModal(true)
  }

  const handleEditArticle = (article) => {
    setEditingArticle(article)
    setArticleForm({
      title: article.title,
      category: article.category,
      difficulty: article.difficulty,
      status: article.status,
      featured: article.featured || false,
      excerpt: article.excerpt,
      content: article.content,
      tags: article.tags || []
    })
    setAttachments(article.attachments || [])
    setFormErrors({})
    setShowEditModal(true)
  }

  const handleViewArticle = (article) => {
    setViewingArticle(article)
    setShowViewModal(true)
  }

  const closeViewModal = () => {
    setShowViewModal(false)
    setViewingArticle(null)
  }

  const handleDeleteArticle = async (id) => {
    if (window.confirm('Are you sure you want to delete this article?')) {
      try {
        setLoading(true)
        await articleService.deleteArticle(id)
        // Reload articles from database
        await loadArticles()
      } catch (error) {
        console.error('Error deleting article:', error)
        alert('Failed to delete article. Please try again.')
      } finally {
        setLoading(false)
      }
    }
  }

  const validateForm = () => {
    const errors = {}
    
    if (!articleForm.title.trim()) {
      errors.title = 'Title is required'
    }
    
    if (!articleForm.category) {
      errors.category = 'Category is required'
    }
    
    if (!articleForm.content.trim()) {
      errors.content = 'Content is required'
    }
    
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleFormSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }
    
    setLoading(true)
    
    try {
      const articleData = {
        title: articleForm.title,
        content: articleForm.content,
        excerpt: articleForm.excerpt,
        category: articleForm.category,
        difficulty: articleForm.difficulty,
        status: articleForm.status,
        featured: articleForm.featured,
        tags: articleForm.tags
      }
      
      let savedArticle
      
      if (editingArticle) {
        // Update existing article
        savedArticle = await articleService.updateArticle(editingArticle.id, articleData)
      } else {
        // Create new article
        savedArticle = await articleService.createArticle(articleData)
      }
      
      // Handle attachment uploads for new files
      if (attachments && attachments.length > 0) {
        const newAttachments = attachments.filter(att => att.isTemporary && att.file)
        
        for (const attachment of newAttachments) {
          try {
            await articleService.uploadAttachment(savedArticle.id, attachment.file, {
              displayName: attachment.display_name || attachment.file_name,
              uploadOrder: attachments.indexOf(attachment)
            })
          } catch (uploadError) {
            console.error('Error uploading attachment:', uploadError)
            // Continue with other attachments even if one fails
          }
        }
      }
      
      // Reload articles from database to show the new/updated article
      await loadArticles()
      
      closeCreateModal()
      closeEditModal()
    } catch (error) {
      console.error('Error saving article:', error)
      setFormErrors({ submit: 'Failed to save article. Please try again.' })
    } finally {
      setLoading(false)
    }
  }

  const closeCreateModal = () => {
    setShowCreateModal(false)
    setArticleForm({
      title: '',
      category: '',
      difficulty: 'beginner',
      status: 'draft',
      featured: false,
      excerpt: '',
      content: '',
      tags: []
    })
    setAttachments([])
    setFormErrors({})
  }

  const closeEditModal = () => {
    setShowEditModal(false)
    setEditingArticle(null)
    setArticleForm({
      title: '',
      category: '',
      difficulty: 'beginner',
      status: 'draft',
      featured: false,
      excerpt: '',
      content: '',
      tags: []
    })
    setAttachments([])
    setFormErrors({})
  }

  const handleFileSelection = async (files) => {
    if (!files || files.length === 0) return
    
    setUploadingFiles(true)
    
    try {
      const newAttachments = Array.from(files).map(file => ({
        id: Date.now() + Math.random(),
        name: file.name,
        filename: file.name,
        display_name: file.name,
        file_name: file.name,
        size: file.size,
        file_size: file.size,
        type: file.type,
        file: file, // Include the actual file object
        isTemporary: true
      }))
      
      setAttachments(prev => [...prev, ...newAttachments])
    } catch (error) {
      console.error('Error uploading files:', error)
    } finally {
      setUploadingFiles(false)
    }
  }

  const handleDeleteAttachment = (attachmentId) => {
    setAttachments(attachments.filter(att => att.id !== attachmentId))
  }

  const handleDownloadAttachment = (attachment) => {
    if (attachment.file_url) {
      window.open(attachment.file_url, '_blank')
    }
  }

  const removeAttachment = (attachmentId) => {
    setAttachments(attachments.filter(att => att.id !== attachmentId))
  }

  const handlePageChange = (page) => {
    if (onPageChange) {
      onPageChange(page)
    } else {
      setLocalCurrentPage(page)
    }
  }

  const handleItemsPerPageChange = (newItemsPerPage) => {
    if (onItemsPerPageChange) {
      onItemsPerPageChange(newItemsPerPage)
    } else {
      setLocalItemsPerPage(newItemsPerPage)
      setLocalCurrentPage(1) // Reset to first page
    }
  }

  const renderPagination = () => {
    return (
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
        <span className="text-sm text-gray-600">
          {`${totalItems} items found • Showing ${startIndex + 1}-${Math.min(endIndex, totalItems)} of ${totalItems}`}
        </span>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Items per page:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={6}>6</option>
              <option value={9}>9</option>
              <option value={12}>12</option>
              <option value={18}>18</option>
            </select>
          </div>
          
          {/* Pagination Controls */}
          {filteredArticles.length > 0 && totalPages > 1 && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let page;
                  if (totalPages <= 5) {
                    page = i + 1;
                  } else {
                    if (currentPage <= 3) {
                      page = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      page = totalPages - 4 + i;
                    } else {
                      page = currentPage - 2 + i;
                    }
                  }
                  
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-2 py-1 text-xs font-medium rounded ${
                        currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Articles</h1>
          <p className="text-gray-600">Manage and organize your cybersecurity articles</p>
        </div>
        {canCreate() && (
          <button
            onClick={handleCreateArticle}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create Article
          </button>
        )}
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <select
              value={selectedDifficulty}
              onChange={(e) => setSelectedDifficulty(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Difficulties</option>
              {difficulties.map(difficulty => (
                <option key={difficulty} value={difficulty}>
                  {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Status</option>
              {statuses.map(status => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {filteredArticles.length > 0 && renderPagination()}

      {/* Articles Grid */}
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {currentArticles.map((article) => (
            <div key={article.id} className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleViewArticle(article)}>
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 break-words">
                      {article.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-3 break-words whitespace-normal">
                      {article.excerpt}
                    </p>
                  </div>
                  <div className="flex gap-1 ml-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleViewArticle(article)
                      }}
                      className="p-2 text-gray-400 hover:text-green-600 transition-colors"
                      title="View"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    {canEdit() && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleEditArticle(article)
                        }}
                        className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    )}
                    {canDelete() && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteArticle(article.id)
                        }}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      <span className="font-medium">{formatArticleDate(article.published_at || article.created_at || article.updated_at)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <User className="h-4 w-4" />
                      <span className="truncate max-w-[120px]">{article.author_name || article.author || 'Unknown Author'}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        article.category === 'cybersecurity' ? 'bg-red-100 text-red-800' :
                        article.category === 'governance' ? 'bg-blue-100 text-blue-800' :
                        article.category === 'compliance' ? 'bg-green-100 text-green-800' :
                        article.category === 'risk-management' ? 'bg-yellow-100 text-yellow-800' :
                        article.category === 'incident-response' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {article.category}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        article.difficulty === 'beginner' ? 'bg-green-100 text-green-800' :
                        article.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {article.difficulty}
                      </span>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      article.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {article.status}
                    </span>
                  </div>
                  
                  {article.tags && article.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {article.tags.slice(0, 3).map((tag, index) => (
                        <span key={index} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                          <Tag className="h-3 w-3" />
                          {tag}
                        </span>
                      ))}
                      {article.tags.length > 3 && (
                        <span className="text-xs text-gray-500">+{article.tags.length - 3} more</span>
                      )}
                    </div>
                  )}
                  
                  {article.attachments && article.attachments.length > 0 && (
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <FileText className="h-4 w-4" />
                      <span>{article.attachments.length} attachment{article.attachments.length !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {filteredArticles.length === 0 && (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No articles found</h3>
            <p className="text-gray-600 mb-4">Try adjusting your search criteria or create a new article.</p>
            <button
              onClick={handleCreateArticle}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Create Article
            </button>
          </div>
        )}
        
        {/* Modals Section */}
        {/* Create Article Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Create New Article</h2>
                  <button
                    onClick={closeCreateModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                <form onSubmit={handleFormSubmit} className="space-y-6">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={articleForm.title}
                      onChange={(e) => setArticleForm({...articleForm, title: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter article title"
                    />
                    {formErrors.title && (
                      <p className="text-red-500 text-sm mt-1">{formErrors.title}</p>
                    )}
                  </div>

                  {/* Category and Difficulty */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category *
                      </label>
                      <select
                        value={articleForm.category}
                        onChange={(e) => setArticleForm({...articleForm, category: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select Category</option>
                        <option value="cybersecurity">Cybersecurity</option>
                        <option value="privacy">Privacy</option>
                        <option value="network-security">Network Security</option>
                        <option value="incident-response">Incident Response</option>
                        <option value="cloud-security">Cloud Security</option>
                      </select>
                      {formErrors.category && (
                        <p className="text-red-500 text-sm mt-1">{formErrors.category}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Difficulty
                      </label>
                      <select
                        value={articleForm.difficulty}
                        onChange={(e) => setArticleForm({...articleForm, difficulty: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="beginner">Beginner</option>
                        <option value="intermediate">Intermediate</option>
                        <option value="advanced">Advanced</option>
                      </select>
                    </div>
                  </div>

                  {/* Status and Featured */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                      </label>
                      <select
                        value={articleForm.status}
                        onChange={(e) => setArticleForm({...articleForm, status: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                      </select>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="featured"
                        checked={articleForm.featured}
                        onChange={(e) => setArticleForm({...articleForm, featured: e.target.checked})}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="featured" className="ml-2 block text-sm text-gray-700">
                        Featured Article
                      </label>
                    </div>
                  </div>

                  {/* Excerpt */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Excerpt
                    </label>
                    <textarea
                      value={articleForm.excerpt}
                      onChange={(e) => setArticleForm({...articleForm, excerpt: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Brief description of the article"
                    />
                  </div>

                  {/* Content */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Content *
                    </label>
                    <textarea
                      value={articleForm.content}
                      onChange={(e) => setArticleForm({...articleForm, content: e.target.value})}
                      rows={10}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Write your article content here..."
                    />
                    {formErrors.content && (
                      <p className="text-red-500 text-sm mt-1">{formErrors.content}</p>
                    )}
                  </div>

                  {/* Tags */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tags
                    </label>
                    <input
                      type="text"
                      value={articleForm.tags.join(', ')}
                      onChange={(e) => setArticleForm({...articleForm, tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag)})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter tags separated by commas"
                    />
                  </div>

                  {/* Attachments */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Attachments
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                      <input
                        type="file"
                        multiple
                        onChange={(e) => {
                          const files = Array.from(e.target.files)
                          const newAttachments = files.map(file => ({
                            id: Date.now() + Math.random(),
                            file_name: file.name,
                            display_name: file.name,
                            file_type: file.type,
                            file_size: file.size,
                            file: file,
                            isTemporary: true
                          }))
                          setAttachments([...attachments, ...newAttachments])
                        }}
                        className="hidden"
                        id="attachment-upload"
                        accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                      />
                      <label htmlFor="attachment-upload" className="cursor-pointer">
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-600">Click to upload files or drag and drop</p>
                        <p className="text-sm text-gray-500 mt-1">PDF, DOC, DOCX, TXT, JPG, PNG up to 10MB each</p>
                      </label>
                    </div>
                    
                    {/* Display uploaded attachments */}
                    {attachments.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <h4 className="text-sm font-medium text-gray-700">Uploaded Files:</h4>
                        {attachments.map((attachment) => (
                          <div key={attachment.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                            <div className="flex items-center space-x-2">
                              <FileText className="w-4 h-4 text-gray-600" />
                              <span className="text-sm text-gray-700">{attachment.file_name}</span>
                              <span className="text-xs text-gray-500">
                                ({(attachment.file_size / 1024 / 1024).toFixed(2)} MB)
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeAttachment(attachment.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Form Errors */}
                  {formErrors.submit && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-red-600 text-sm">{formErrors.submit}</p>
                    </div>
                  )}

                  {/* Form Actions */}
                  <div className="flex justify-end gap-3 pt-6 border-t">
                    <button
                      type="button"
                      onClick={closeCreateModal}
                      className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Creating...' : 'Create Article'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Edit Article Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Edit Article</h2>
                  <button
                    onClick={closeEditModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                <form onSubmit={handleFormSubmit} className="space-y-6">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={articleForm.title}
                      onChange={(e) => setArticleForm({...articleForm, title: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter article title"
                    />
                    {formErrors.title && (
                      <p className="text-red-500 text-sm mt-1">{formErrors.title}</p>
                    )}
                  </div>

                  {/* Category and Difficulty */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category *
                      </label>
                      <select
                        value={articleForm.category}
                        onChange={(e) => setArticleForm({...articleForm, category: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select Category</option>
                        <option value="cybersecurity">Cybersecurity</option>
                        <option value="privacy">Privacy</option>
                        <option value="network-security">Network Security</option>
                        <option value="incident-response">Incident Response</option>
                        <option value="cloud-security">Cloud Security</option>
                      </select>
                      {formErrors.category && (
                        <p className="text-red-500 text-sm mt-1">{formErrors.category}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Difficulty
                      </label>
                      <select
                        value={articleForm.difficulty}
                        onChange={(e) => setArticleForm({...articleForm, difficulty: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="beginner">Beginner</option>
                        <option value="intermediate">Intermediate</option>
                        <option value="advanced">Advanced</option>
                      </select>
                    </div>
                  </div>

                  {/* Status and Featured */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                      </label>
                      <select
                        value={articleForm.status}
                        onChange={(e) => setArticleForm({...articleForm, status: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                      </select>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="featured-edit"
                        checked={articleForm.featured}
                        onChange={(e) => setArticleForm({...articleForm, featured: e.target.checked})}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="featured-edit" className="ml-2 block text-sm text-gray-700">
                        Featured Article
                      </label>
                    </div>
                  </div>

                  {/* Excerpt */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Excerpt
                    </label>
                    <textarea
                      value={articleForm.excerpt}
                      onChange={(e) => setArticleForm({...articleForm, excerpt: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Brief description of the article"
                    />
                  </div>

                  {/* Content */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Content *
                    </label>
                    <textarea
                      value={articleForm.content}
                      onChange={(e) => setArticleForm({...articleForm, content: e.target.value})}
                      rows={10}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Article content"
                    />
                    {formErrors.content && (
                      <p className="text-red-500 text-sm mt-1">{formErrors.content}</p>
                    )}
                  </div>

                  {/* Tags */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tags
                    </label>
                    <input
                      type="text"
                      value={articleForm.tags.join(', ')}
                      onChange={(e) => setArticleForm({...articleForm, tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag)})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter tags separated by commas"
                    />
                  </div>

                  {/* File Attachments */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Attachments
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                      <div className="text-center">
                        <Upload className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="mt-4">
                          <label htmlFor="file-upload-edit" className="cursor-pointer">
                            <span className="mt-2 block text-sm font-medium text-gray-900">
                              Drop files here or click to upload
                            </span>
                            <input
                              id="file-upload-edit"
                              name="file-upload-edit"
                              type="file"
                              multiple
                              className="sr-only"
                              onChange={(e) => handleFileSelection(e.target.files)}
                            />
                          </label>
                          <p className="mt-1 text-xs text-gray-500">
                            PDF, DOC, DOCX, XLS, XLSX up to 10MB each
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Attachment List */}
                    {attachments.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {attachments.map((attachment) => (
                          <div key={attachment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center">
                              <FileText className="h-5 w-5 text-gray-400 mr-3" />
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {attachment.display_name || attachment.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {attachment.file_size ? `${(attachment.file_size / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {attachment.file_url && (
                                <button
                                  type="button"
                                  onClick={() => handleDownloadAttachment(attachment)}
                                  className="p-1 text-blue-600 hover:text-blue-800"
                                  title="Download"
                                >
                                  <Download className="h-4 w-4" />
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => handleDeleteAttachment(attachment.id)}
                                className="p-1 text-red-600 hover:text-red-800"
                                title="Remove"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {formErrors.submit && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-red-600 text-sm">{formErrors.submit}</p>
                    </div>
                  )}

                  {/* Form Actions */}
                  <div className="flex justify-end gap-3 pt-6 border-t">
                    <button
                      type="button"
                      onClick={closeEditModal}
                      className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Updating...' : 'Update Article'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
         )}

        {/* View Article Modal */}
        {showViewModal && viewingArticle && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">{viewingArticle.title}</h2>
                  <button
                    onClick={closeViewModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                <div className="space-y-6">
                  {/* Article Meta Information */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">Category:</span>
                        <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                          viewingArticle.category === 'cybersecurity' ? 'bg-red-100 text-red-800' :
                          viewingArticle.category === 'governance' ? 'bg-blue-100 text-blue-800' :
                          viewingArticle.category === 'compliance' ? 'bg-green-100 text-green-800' :
                          viewingArticle.category === 'risk-management' ? 'bg-yellow-100 text-yellow-800' :
                          viewingArticle.category === 'incident-response' ? 'bg-purple-100 text-purple-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {viewingArticle.category}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Difficulty:</span>
                        <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                          viewingArticle.difficulty === 'beginner' ? 'bg-green-100 text-green-800' :
                          viewingArticle.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {viewingArticle.difficulty}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Status:</span>
                        <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                          viewingArticle.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {viewingArticle.status}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Date:</span>
                        <span className="ml-2 text-gray-600">{formatArticleDate(viewingArticle.published_at || viewingArticle.created_at || viewingArticle.updated_at)}</span>
                      </div>
                    </div>
                    {(viewingArticle.author_name || viewingArticle.author) && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <span className="font-medium text-gray-700">Author:</span>
                        <span className="ml-2 text-gray-600">{viewingArticle.author_name || viewingArticle.author}</span>
                      </div>
                    )}
                  </div>

                  {/* Article Excerpt */}
                  {viewingArticle.excerpt && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Summary</h3>
                      <p className="text-gray-700 bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                        {viewingArticle.excerpt}
                      </p>
                    </div>
                  )}

                  {/* Article Content */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Content</h3>
                    <div className="prose max-w-none">
                      <div className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                        {viewingArticle.content}
                      </div>
                    </div>
                  </div>

                  {/* Tags */}
                  {viewingArticle.tags && viewingArticle.tags.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Tags</h3>
                      <div className="flex flex-wrap gap-2">
                        {viewingArticle.tags.map((tag, index) => (
                          <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Attachments */}
                  {viewingArticle.attachments && viewingArticle.attachments.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Attachments</h3>
                      <div className="space-y-2">
                        {viewingArticle.attachments.map((attachment) => (
                          <div key={attachment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center">
                              <FileText className="h-5 w-5 text-gray-400 mr-3" />
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {attachment.display_name || attachment.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {attachment.file_size ? `${(attachment.file_size / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'}
                                </p>
                              </div>
                            </div>
                            {attachment.file_url && (
                              <button
                                onClick={() => window.open(attachment.file_url, '_blank')}
                                className="p-2 text-blue-600 hover:text-blue-800"
                                title="Download"
                              >
                                <Download className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Modal Actions */}
                <div className="flex justify-end gap-3 pt-6 border-t mt-6">
                  {canEdit() && (
                    <button
                      onClick={() => {
                        closeViewModal()
                        handleEditArticle(viewingArticle)
                      }}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                      Edit Article
                    </button>
                  )}
                  <button
                    onClick={closeViewModal}
                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    </div>
  )
}

export default Articles